import { AuthData, getAdminMagicSecret } from './auth';
import { getAdminNotifications, getYellowfeedsQueueCount } from './api';
import { isSuperuser } from '../schema-copy/capabilities';

const POLLING_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

// Yellowfeeds' review queue (claims + listing changes of its publishers) is polled less often, and the
// last result is kept in localStorage, so reloads and other tabs reuse it instead of asking again.
const YELLOWFEEDS_INTERVAL_MS = 15 * 60 * 1000;
const YELLOWFEEDS_CACHE_KEY = 'adminYellowfeedsQueue';

interface YellowfeedsCache { count: number; at: number }

function readYellowfeedsCache(): YellowfeedsCache | undefined {
  try {
    const cache = JSON.parse(window.localStorage.getItem(YELLOWFEEDS_CACHE_KEY) || 'null');
    return typeof cache?.count == 'number' && typeof cache?.at == 'number' ? cache : undefined;
  } catch {
    return undefined;
  }
}

class AdminNotificationEngine {
  private count = 0;
  private inactiveCount = 0;
  private reportsCount = 0;
  private urgentReportsCount = 0;
  private yellowfeedsCount = readYellowfeedsCache()?.count ?? 0;
  private yellowfeedsTimer: any = null;
  private yellowfeedsSubscribers = new Set<(count: number) => void>();
  private authData: AuthData | undefined = undefined;
  private intervalId: any = null;
  // True when the admin magic secret hasn't been entered yet: we can't poll, so the sidebar shows a
  // warning in place of the counts instead of silently showing nothing.
  private needsMagic = false;
  private subscribers = new Set<(count: number) => void>();
  private inactiveSubscribers = new Set<(count: number) => void>();
  private reportsSubscribers = new Set<(count: number) => void>();
  private needsMagicSubscribers = new Set<(needsMagic: boolean) => void>();

  /**
   * Get the current feeds-to-process count.
   */
  getCount(): number {
    return this.count;
  }

  /**
   * Get the current count of feeds that went inactive in the last 7 days.
   */
  getInactiveCount(): number {
    return this.inactiveCount;
  }

  /**
   * Get the current count of pending content reports awaiting review.
   */
  getReportsCount(): number {
    return this.reportsCount;
  }

  /**
   * Get the count of pending reports that are legally urgent (CSAM).
   */
  getUrgentReportsCount(): number {
    return this.urgentReportsCount;
  }

  /**
   * Register a subscriber callback to receive feeds-to-process count updates.
   * Returns an unsubscribe function.
   */
  onCountChange(cb: (count: number) => void): () => void {
    this.subscribers.add(cb);
    // Call immediately with the current count
    cb(this.count);

    return () => {
      this.subscribers.delete(cb);
    };
  }

  /**
   * Register a subscriber callback to receive recently-inactive-feeds count updates.
   * Returns an unsubscribe function.
   */
  onInactiveCountChange(cb: (count: number) => void): () => void {
    this.inactiveSubscribers.add(cb);
    cb(this.inactiveCount);

    return () => {
      this.inactiveSubscribers.delete(cb);
    };
  }

  /** Whether the admin magic secret still needs to be entered (so polling is blocked). */
  getNeedsMagic(): boolean {
    return this.needsMagic;
  }

  /**
   * Register a subscriber for the "needs admin secret" warning state.
   * Returns an unsubscribe function.
   */
  onNeedsMagicChange(cb: (needsMagic: boolean) => void): () => void {
    this.needsMagicSubscribers.add(cb);
    cb(this.needsMagic);
    return () => {
      this.needsMagicSubscribers.delete(cb);
    };
  }

  private setNeedsMagic(value: boolean) {
    if (this.needsMagic !== value) {
      this.needsMagic = value;
      this.needsMagicSubscribers.forEach((s) => { try { s(value); } catch (e) { console.error('Error in needsMagic subscriber', e); } });
    }
  }

  /**
   * Re-poll immediately — e.g. right after the admin secret is entered — so the warning clears
   * without waiting for the next interval tick.
   */
  recheckNow() {
    this.poll();
    if (this.authData && !readYellowfeedsCache()) this.scheduleYellowfeeds();
  }

  /** The size of Yellowfeeds' review queue (claims and listing changes to review). */
  getYellowfeedsCount(): number {
    return this.yellowfeedsCount;
  }

  /** Register a subscriber for the Yellowfeeds queue size. Returns an unsubscribe function. */
  onYellowfeedsCountChange(cb: (count: number) => void): () => void {
    this.yellowfeedsSubscribers.add(cb);
    cb(this.yellowfeedsCount);
    return () => {
      this.yellowfeedsSubscribers.delete(cb);
    };
  }

  private setYellowfeedsCount(count: number) {
    if (this.yellowfeedsCount == count) return;
    this.yellowfeedsCount = count;
    this.yellowfeedsSubscribers.forEach((s) => { try { s(count); } catch (e) { console.error('Error in yellowfeeds-count subscriber', e); } });
  }

  /**
   * Polls the Yellowfeeds queue once the cached size (maybe from another tab) is older than the
   * interval, but not before minDelay: after a failed poll (or without the admin secret) there is no
   * fresh cache, and the next try waits a whole interval.
   */
  private scheduleYellowfeeds(minDelay = 0) {
    clearTimeout(this.yellowfeedsTimer);
    const cache = readYellowfeedsCache();
    if (cache) this.setYellowfeedsCount(cache.count);
    const wait = cache ? Math.max(0, cache.at + YELLOWFEEDS_INTERVAL_MS - Date.now()) : 0;
    this.yellowfeedsTimer = setTimeout(async () => {
      const current = readYellowfeedsCache();
      if (!current || Date.now() - current.at >= YELLOWFEEDS_INTERVAL_MS) await this.pollYellowfeeds();
      if (this.authData) this.scheduleYellowfeeds(YELLOWFEEDS_INTERVAL_MS);
    }, Math.max(minDelay, wait));
  }

  private async pollYellowfeeds() {
    const magic = getAdminMagicSecret();
    if (!this.authData || !magic) return;
    try {
      const { claimCount, changeCount } = await getYellowfeedsQueueCount(this.authData, magic);
      const count = claimCount + changeCount;
      try { window.localStorage.setItem(YELLOWFEEDS_CACHE_KEY, JSON.stringify({ count, at: Date.now() })); } catch { /* not cached */ }
      this.setYellowfeedsCount(count);
    } catch (e) {
      console.warn('Failed to poll the Yellowfeeds queue:', e);
    }
  }

  /**
   * Register a subscriber callback to receive pending-report count updates.
   * Returns an unsubscribe function.
   */
  onReportsCountChange(cb: (count: number) => void): () => void {
    this.reportsSubscribers.add(cb);
    cb(this.reportsCount);

    return () => {
      this.reportsSubscribers.delete(cb);
    };
  }

  private notify() {
    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(this.count);
      } catch (e) {
        console.error('Error in admin count subscriber callback:', e);
      }
    });
  }

  private notifyInactive() {
    this.inactiveSubscribers.forEach((subscriber) => {
      try {
        subscriber(this.inactiveCount);
      } catch (e) {
        console.error('Error in admin inactive-count subscriber callback:', e);
      }
    });
  }

  private notifyReports() {
    this.reportsSubscribers.forEach((subscriber) => {
      try {
        subscriber(this.reportsCount);
      } catch (e) {
        console.error('Error in admin reports-count subscriber callback:', e);
      }
    });
  }

  /**
   * Decrements the local count by 1 but guarantees the count never drops below 1.
   */
  decrementCount() {
    if (this.count > 0) {
      this.count = Math.max(1, this.count - 1);
      this.notify();
    }
  }

  /**
   * Resets the local count to 0, completely removing any badges.
   */
  resetCountToZero() {
    if (this.count !== 0) {
      this.count = 0;
      this.notify();
    }
  }

  /**
   * Resets the inactive-feeds count to 0 (used on logout).
   */
  private resetInactiveToZero() {
    if (this.inactiveCount !== 0) {
      this.inactiveCount = 0;
      this.notifyInactive();
    }
  }

  /**
   * Locally decrement the pending-reports count after an admin resolves a report,
   * so the sidebar badge (and the red Admin highlight) update immediately. The next
   * poll reconciles the exact counts.
   */
  decrementReportsCount(wasUrgent = false) {
    if (this.reportsCount > 0) {
      this.reportsCount -= 1;
      if (wasUrgent && this.urgentReportsCount > 0) this.urgentReportsCount -= 1;
      this.notifyReports();
    }
  }

  /**
   * Resets the reports count to 0 (used on logout).
   */
  private resetReportsToZero() {
    if (this.reportsCount !== 0 || this.urgentReportsCount !== 0) {
      this.reportsCount = 0;
      this.urgentReportsCount = 0;
      this.notifyReports();
    }
  }

  /**
   * Enable polling if authenticated and the user is a superuser.
   */
  enable(authData: AuthData) {
    // Already enabled for this user — keep the running poll instead of restarting
    // (StrictMode re-runs App's mount effect, and setLoggedIn may repeat the auth).
    if (this.authData?.id == authData.id) return;

    this.disable(false); // Clean up any existing polling first (keeping the cached Yellowfeeds count)

    this.authData = authData;

    if (isSuperuser(authData.selectedMembership?.capabilities)) {
      this.poll();
      this.intervalId = setInterval(() => {
        this.poll();
      }, POLLING_INTERVAL_MS);
      this.scheduleYellowfeeds();
    }
  }

  /**
   * Disable polling and clear active states on logout (forget: also the cached Yellowfeeds count).
   */
  disable(forget = true) {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    clearTimeout(this.yellowfeedsTimer);
    this.yellowfeedsTimer = null;
    this.authData = undefined;
    this.setNeedsMagic(false);
    this.setYellowfeedsCount(0);
    if (forget)
      try { window.localStorage.removeItem(YELLOWFEEDS_CACHE_KEY); } catch { /* nothing cached */ }
    this.resetCountToZero();
    this.resetInactiveToZero();
    this.resetReportsToZero();
  }

  /**
   * Make the API call to update the count.
   */
  private async poll() {
    if (!this.authData) return;

    const magic = getAdminMagicSecret() || '';
    if (!magic) {
      // No admin secret entered yet — don't poll (it would 401 with "Missing magic parameter").
      // The sidebar shows a warning instead; recheckNow() re-polls once the secret is entered.
      this.setNeedsMagic(true);
      return;
    }
    this.setNeedsMagic(false);

    try {
      const response = await getAdminNotifications(this.authData, magic);
      if (response && typeof response.feedsToProcess === 'number') {
        const newCount = response.feedsToProcess;
        if (this.count !== newCount) {
          this.count = newCount;
          this.notify();
        }
      }
      if (response && typeof response.recentlyInactiveFeeds === 'number') {
        if (this.inactiveCount !== response.recentlyInactiveFeeds) {
          this.inactiveCount = response.recentlyInactiveFeeds;
          this.notifyInactive();
        }
      }
      if (response && typeof response.reportsToReview === 'number') {
        this.urgentReportsCount = response.urgentReports ?? 0;
        if (this.reportsCount !== response.reportsToReview) {
          this.reportsCount = response.reportsToReview;
          this.notifyReports();
        }
      }
    } catch (e) {
      console.warn('Failed to poll admin notifications:', e);
    }
  }
}

export const adminNotificationEngine = new AdminNotificationEngine();
